/**
 * Mio analytics helpers (2026-06-09 expanded).
 *
 * Dual-firing pattern: every helper sends the same event to TWO
 * independent destinations so we never lose data:
 *
 *   1. Google Analytics 4 via `@next/third-parties/google`
 *      (frontend product analytics, dashboards, Realtime view).
 *   2. Mio's own analytics DB via POST /api/telemetry/click using
 *      `navigator.sendBeacon` (server-owned data trail, survives
 *      ad-blockers / GA outages / future privacy regulation; can
 *      be joined to server-side data in SQL).
 *
 * Three concerns centralised here so consumer code stays clean:
 *
 *   1. PII discipline. Mio MUST NOT send personally identifiable
 *      information to Google OR our own telemetry endpoint
 *      (email, full name, full postcode, Clerk user_id, IP).
 *      The helper signatures only accept non-PII fields. TypeScript
 *      enforces it at compile time.
 *
 *   2. No-op fallback. When NEXT_PUBLIC_GA_ID is not set (local dev,
 *      preview deploys) the GA arm silently no-ops. The backend
 *      arm always fires because the backend endpoint always exists.
 *
 *   3. Single source of truth for event names + property shapes.
 *      Renaming an event in one place updates every call site.
 *
 * GA4 event-name conventions:
 *   - snake_case
 *   - past-tense verbs ("search_submitted", "retailer_buy_clicked")
 *   - avoid GA4-reserved names (page_view, scroll, click, etc.)
 *     auto-fired by Enhanced Measurement.
 *
 * Cite this file from any new tracking call site so the helpers
 * stay in sync with consumers.
 */

import { lookupClaim, type ClaimRef } from "@/lib/claimIndex";
import { isOutsideArrival, visitSource, type VisitSource } from "@/lib/visitSource";

// 2026-07-24: the Google Analytics arm is RESTORED (owner decision).
// Rationale is documented in full in app/layout.tsx: GA4's gtag is a
// measurement library with no ability to run custom JavaScript, no GTM
// container has ever existed in this repo, and the 07-12 account-security
// fix did not stop the overlay returning - so the 07-20 conclusion that
// gtag was the vector does not survive review.
//
// Both arms fire for every event, and they are NOT redundant:
//   - Google  -> audience and behaviour reporting, automatic dimensions
//                (country, city, device, source), zero maintenance.
//   - Ours    -> the only copy that can be joined to claims/retailers in
//                SQL, and the only one that survives ad-blockers and
//                Safari private browsing (proven 2026-07-24: a private
//                session reached us from AU and was invisible to Google).
// The SERVER-side GA4 forwarder is off, so nothing is double-counted.

type SearchSource = "home" | "compare" | "c_slug" | "c_list_slug" | "other";

type PageKind =
  | "home"
  | "product"
  | "list"
  | "comparison"
  | "compare_streaming"
  | "other";

type ShareChannel =
  | "open"
  | "native"
  | "imessage"
  | "whatsapp"
  | "x"
  | "copy"
  | "email"
  | "facebook"
  | "more";

/**
 * Dual-fire: send to GA (when enabled) AND to Mio's backend
 * telemetry endpoint. Both arms never throw; analytics MUST NOT
 * crash the page.
 *
 * Backend arm uses `navigator.sendBeacon` so the request is:
 *   - Non-blocking. Returns immediately; the browser sends it
 *     asynchronously in the background.
 *   - Reliable through navigation. The browser guarantees the
 *     beacon completes even if the user clicks a Buy link and
 *     navigates away to the retailer site mid-request.
 *   - JSON encoded as a Blob with the correct MIME so FastAPI
 *     parses it via the standard request body decoder.
 */
/**
 * GA4 accepts at most 25 parameters per event, parameter names up to 40
 * characters and string values up to 100; anything longer is silently
 * truncated on Google's side. Trim here instead, so what Google stores is
 * predictable rather than cut at an arbitrary point. Null/undefined are
 * dropped so empty optionals never create noise dimensions.
 */
function gaParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  let count = 0;
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (count >= 25) break;
    out[key.slice(0, 40)] =
      typeof value === "string" ? value.slice(0, 100) : value;
    count += 1;
  }
  return out;
}

/** B-240 step 4 (2026-09-18): the page declares its layout on <main
 *  data-layout="one_card"> only while the one-card switch is on. Every event
 *  then carries `layout` so old and new can be compared in our own events
 *  table. With the switch off no attribute exists and nothing is added. */
function pageLayout(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const v = document.querySelector("main[data-layout]")?.getAttribute("data-layout");
    return v && v.length <= 16 ? v : null;
  } catch {
    return null;
  }
}

function track(eventName: string, rawParams: Record<string, unknown>): void {
  const layout = pageLayout();
  const params = layout ? { ...rawParams, layout } : rawParams;
  // Arm 1: Google Analytics (2026-07-24). gtag is installed by
  // <GoogleAnalytics> in app/layout.tsx and is absent in local dev and
  // previews (no NEXT_PUBLIC_GA_ID), so the guard below makes this a
  // no-op there rather than an error. Event names are deliberately NOT
  // GA4-reserved (page_view, scroll, click, session_start, first_visit,
  // view_search_results, ...), so these arrive as distinct custom events
  // alongside Google's automatic ones instead of colliding with them.
  // PII discipline is enforced by the helper signatures below: no email,
  // name, postcode or user id ever reaches this function.
  if (typeof window !== "undefined") {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag === "function") {
      try {
        w.gtag("event", eventName, gaParams(params));
      } catch {
        // Analytics must never break the page.
      }
    }
  }

  // Arm 2: first-party backend. Survives ad-blockers and Safari private
  // browsing (which block Google outright), and is the only copy that can
  // be joined to claims/retailers in SQL. Never removed.
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const body = JSON.stringify({
        event_type: eventName,
        ...params,
      });
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon("/api/telemetry/click", blob);
    } catch {
      // Swallow.
    }
  }
}

// ---------- Search funnel ----------------------------------------------

export function trackSearchSubmitted(args: {
  query: string;
  source: SearchSource;
}): void {
  const q = args.query.trim();
  track("search_submitted", {
    query: q,
    source: args.source,
    query_length: q.length,
  });
}

export function trackSearchCompleted(args: {
  query: string;
  retailerCount: number;
  successCount: number;
  lowestTfpAud: number | null;
  cacheHit: boolean;
}): void {
  track("search_completed", {
    query: args.query,
    retailer_count: args.retailerCount,
    success_count: args.successCount,
    lowest_tfp_aud: args.lowestTfpAud,
    cache_hit: args.cacheHit,
  });
}

export function trackSearchFailed(args: {
  query: string;
  reason: string;
}): void {
  track("search_failed", {
    query: args.query,
    reason: args.reason,
  });
}

// ---------- Discovery --------------------------------------------------

export function trackTrendingChipClicked(args: {
  chipLabel: string;
  position: number;
}): void {
  track("trending_chip_clicked", {
    chip_label: args.chipLabel,
    position: args.position,
  });
}

// ---------- Conversion intent ------------------------------------------

/**
 * Fired when the user clicks Buy on any comparison row (Hero, expanded,
 * compact, list-card). The primary conversion event Mio cares about.
 */
/**
 * B17 (2026-07-18): did the shopper act on this comparison view?
 *
 * Module-level because the walk-away tracker (a page-level hook) and the
 * action buttons (deep in the row components) have no shared React state,
 * and threading a context through every card would be far more invasive
 * for a single boolean. Reset on each comparison view.
 */
let _comparisonActionTaken = false;

/** Called by every action that means "this was NOT a walk-away". */
/** B-169 (2026-08-30): a shopper opened a saved comparison page. Two arms
 * as always: GA4 for funnels, and the first-party beacon, which is what
 * appends the view (with the offers that were on the page, resolved
 * server-side) to the shopper action ledger. `signedIn` tells the backend
 * the signed-in path already recorded this view, so it is not counted
 * twice. Fires once per page. */
export function trackComparisonViewed(args: {
  productSlug: string;
  signedIn: boolean;
}): void {
  track("comparison_viewed", {
    product_slug: args.productSlug,
    source: args.signedIn ? "signed_in" : "anon",
  });
}

export function markComparisonAction(): void {
  _comparisonActionTaken = true;
}

/** True when the shopper has acted on the current comparison view. */
export function hasComparisonAction(): boolean {
  return _comparisonActionTaken;
}

/** Reset at the start of each comparison view. */
export function resetComparisonAction(): void {
  _comparisonActionTaken = false;
}

/**
 * B17: the walk-away. The shopper looked at a comparison and left without
 * buying, negotiating, sharing or watching. Carries the best price they
 * saw, so we can later ask "at what price do people walk away?" (F53).
 *
 * Fired at most once per view, on page-hide, via sendBeacon so it survives
 * the tab closing.
 */
export function trackComparisonWalkedAway(args: {
  productSlug: string | null;
  pageKind: PageKind;
  retailerCount: number | null;
  lowestTfpAud: number | null;
  dwellMs: number;
  scrollPct: number;
}): void {
  track("comparison_walked_away", {
    product_slug: args.productSlug,
    page_kind: args.pageKind,
    retailer_count: args.retailerCount,
    lowest_tfp_aud: args.lowestTfpAud,
    dwell_ms: args.dwellMs,
    scroll_pct: args.scrollPct,
  });
}

// ---------- C20 / C21: the sign-up gate --------------------------------

/**
 * Origin marker for the auth gate.
 *
 * sessionStorage (not a module variable) because the Clerk flow can span
 * several steps and a page reload, and a module variable would be lost.
 * Scoped to the tab and cleared on completion.
 */
const _SIGNUP_ORIGIN_KEY = "octo_signup_origin";
/**
 * Marker TTL. Without it, a shopper who hits the Negotiate gate, wanders off,
 * and signs in from the header an hour later would be mis-attributed as a
 * negotiate conversion. 30 minutes is far longer than a real sign-up takes
 * and short enough to stop that.
 */
const _SIGNUP_ORIGIN_TTL_MS = 30 * 60 * 1000;

// "search_wall" (2026-10-01): the sign-in screen a signed-out visitor gets
// for a NEW search (components/comparison/SignInToSearchGate.tsx).
type SignupSource = "negotiate" | "header" | "search_wall" | "other";
type SignupMethod = "sign_up" | "sign_in";

function _writeSignupOrigin(source: SignupSource): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      _SIGNUP_ORIGIN_KEY,
      JSON.stringify({ source, at: Date.now() }),
    );
    // 2026-10-02: a second copy for the account record (AuthRecorder), which
    // trackSignupCompleted does not consume.
    window.sessionStorage.setItem(
      _AUTH_ENTRY_KEY,
      JSON.stringify({ source, at: Date.now() }),
    );
  } catch {
    // Private mode / storage disabled: attribution degrades to "other".
  }
}

const _AUTH_ENTRY_KEY = "octo_auth_entry";

/** Where in the site the current sign-in started (search_wall, negotiate,
 *  header), or null. Read and cleared by the account record. */
export function takeAuthEntry(): SignupSource | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(_AUTH_ENTRY_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(_AUTH_ENTRY_KEY);
    const parsed = JSON.parse(raw) as { source?: string; at?: number };
    if (!parsed?.at || Date.now() - parsed.at > _SIGNUP_ORIGIN_TTL_MS) return null;
    return (parsed.source as SignupSource) ?? null;
  } catch {
    return null;
  }
}

/** Reads + clears the marker. Returns null when absent or expired. */
function _takeSignupOrigin(): { source: SignupSource; at: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(_SIGNUP_ORIGIN_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(_SIGNUP_ORIGIN_KEY);
    const parsed = JSON.parse(raw) as { source?: string; at?: number };
    if (!parsed?.at || Date.now() - parsed.at > _SIGNUP_ORIGIN_TTL_MS) {
      return null;
    }
    return { source: (parsed.source as SignupSource) ?? "other", at: parsed.at };
  } catch {
    return null;
  }
}

/**
 * C20: the auth wall was shown to an anonymous shopper. Today this fires from
 * the Negotiate button, which is the gate we care about: it is the single
 * biggest drop-off in the funnel.
 */
export function trackSignupPromptShown(args: {
  source: SignupSource;
  retailerSlug?: string | null;
  productSlug?: string | null;
  pageKind: PageKind;
  /** The search the visitor typed (search wall only). */
  query?: string | null;
}): void {
  _writeSignupOrigin(args.source);
  const q = (args.query ?? "").trim().slice(0, 200);
  track("signup_prompt_shown", {
    source: args.source,
    retailer_slug: args.retailerSlug ?? null,
    product_slug: args.productSlug ?? null,
    page_kind: args.pageKind,
    query: q || null,
  });
}

/**
 * B-337 (2026-10-01): one event per visit, on its first page: where the
 * visitor came from (lib/visitSource.ts). "Per visit" = once per browser
 * tab: a reload or a click to another page in the same tab does not fire it
 * again; a new tab does. The anonymous sticker cookie is minted before this
 * runs, so the row carries the same session id as the visitor's clicks.
 */
const _VISIT_KEY = "octo_visit_logged";

export function trackVisitStarted(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(_VISIT_KEY)) return;
    window.sessionStorage.setItem(_VISIT_KEY, "1");
  } catch {
    // Storage blocked (some private modes): still record the visit once per
    // page load rather than never.
  }
  const s = visitSource(window.location.href, document.referrer || "");
  rememberArrival(s);
  const { pageKind, productSlug } = deriveContext(s.landing_path);
  track("visit_started", {
    referrer_host: s.referrer_host,
    utm_source: s.utm_source,
    utm_medium: s.utm_medium,
    utm_campaign: s.utm_campaign,
    click_id: s.click_id,
    landing_path: s.landing_path,
    page_kind: s.landing_path === "/" ? "home" : pageKind,
    product_slug: productSlug,
  });
}

/**
 * 2026-10-02 (owner): each browser remembers its own arrivals, so a sign-up or
 * sign-in can be recorded with where the person came from, even when it
 * happens in a later tab or a later visit:
 *   first visit  the first arrival this browser ever recorded, whatever it was
 *   last source  the latest arrival that came from outside with a known source
 * First-party browser storage only; no personal data (hosts, tags, paths).
 */
const _FIRST_VISIT_KEY = "octo_first_visit";
const _LAST_SOURCE_KEY = "octo_last_source";

export interface Arrival extends VisitSource {
  at: string;
}

function rememberArrival(s: VisitSource): void {
  if (typeof window === "undefined") return;
  try {
    const a: Arrival = { ...s, at: new Date().toISOString() };
    if (!window.localStorage.getItem(_FIRST_VISIT_KEY)) {
      window.localStorage.setItem(_FIRST_VISIT_KEY, JSON.stringify(a));
    }
    if (isOutsideArrival(s, window.location.host)) {
      window.localStorage.setItem(_LAST_SOURCE_KEY, JSON.stringify(a));
    }
  } catch {
    // Storage blocked: the account record then carries no arrival.
  }
}

/** The two remembered arrivals of this browser (either may be null). */
export function readArrivals(): { firstVisit: Arrival | null; lastSource: Arrival | null } {
  const read = (k: string): Arrival | null => {
    try {
      const raw = typeof window === "undefined" ? null : window.localStorage.getItem(k);
      return raw ? (JSON.parse(raw) as Arrival) : null;
    } catch {
      return null;
    }
  };
  return { firstVisit: read(_FIRST_VISIT_KEY), lastSource: read(_LAST_SOURCE_KEY) };
}

/**
 * C21: the shopper got through the gate. Paired with C20 this gives the
 * conversion rate. `source` comes from the marker written when the gate was
 * raised, so a negotiate-driven sign-up is distinguishable from a casual one.
 */
export function trackSignupCompleted(args: { method: SignupMethod }): void {
  const origin = _takeSignupOrigin();
  track("signup_completed", {
    method: args.method,
    source: origin?.source ?? "other",
    elapsed_ms: origin ? Math.max(0, Date.now() - origin.at) : null,
  });
}

export function trackRetailerBuyClicked(args: {
  retailerSlug: string;
  tfpAud: number;
  productSlug: string | null;
  pageKind: PageKind;
  /**
   * E46: the negotiation this purchase belongs to, when the shopper had
   * already negotiated for this retailer + product. Resolved automatically by
   * the path-aware wrapper below; pass explicitly only from Hero, which calls
   * this directly.
   */
  claim?: ClaimRef | null;
}): void {
  markComparisonAction();
  track("retailer_buy_clicked", {
    retailer_slug: args.retailerSlug,
    tfp_aud: args.tfpAud,
    product_slug: args.productSlug,
    page_kind: args.pageKind,
    // Null for the common case (no prior negotiation); the backend only
    // stores non-null fields, so ordinary buy clicks are unchanged.
    transaction_id: args.claim?.transactionId ?? null,
    claim_status: args.claim?.status ?? null,
  });
}

/**
 * Fired when the user clicks Negotiate (the Price match / Negotiate
 * button) on any retailer card. Captures which retailer the user
 * wants to negotiate with, regardless of whether the negotiation
 * agent has shipped yet.
 */
export function trackNegotiateClicked(args: {
  retailerSlug: string;
  tfpAud: number;
  productSlug: string | null;
  pageKind: PageKind;
}): void {
  markComparisonAction();
  track("negotiate_clicked", {
    retailer_slug: args.retailerSlug,
    tfp_aud: args.tfpAud,
    product_slug: args.productSlug,
    page_kind: args.pageKind,
  });
}

/**
 * Fired when the user clicks Share (savings receipt) on any retailer
 * card. The `channel` parameter discriminates how the share was
 * completed:
 *
 *   "open"      main Share button clicked, modal not yet opened
 *   "native"    Web Share API (mobile native share sheet) accepted
 *   "imessage"  iMessage channel button clicked in desktop modal
 *   "whatsapp"  WhatsApp channel button clicked in desktop modal
 *   "x"         X (Twitter) channel button clicked in desktop modal
 *   "copy"      Copy link button clicked in desktop modal
 *
 * Multiple events can fire per share session (e.g., main "open" then
 * a later "copy" if the user opens the modal and copies the link).
 */
export function trackShareClicked(args: {
  retailerSlug: string;
  tfpAud: number;
  productSlug: string | null;
  pageKind: PageKind;
  channel: ShareChannel;
}): void {
  markComparisonAction();
  track("share_clicked", {
    retailer_slug: args.retailerSlug,
    tfp_aud: args.tfpAud,
    product_slug: args.productSlug,
    page_kind: args.pageKind,
    channel: args.channel,
  });
}

// ---------- Path-aware helpers ----------------------------------------

/**
 * Derive page_kind + product_slug from the current URL so callers
 * inside ComparisonRow / Hero / their nested sub-components can fire
 * tracking with a single call instead of duplicating the path-parsing
 * logic.
 */
function deriveContext(pathname: string): {
  pageKind: PageKind;
  productSlug: string | null;
} {
  const pageKind: PageKind = pathname.startsWith("/c/list/")
    ? "list"
    : pathname.startsWith("/c/vs/")
      ? "comparison"
      : pathname.startsWith("/c/")
        ? "product"
        : pathname.startsWith("/compare")
          ? "compare_streaming"
          : "other";
  const productSlug = pathname.startsWith("/c/")
    ? pathname.replace(/^\/c\/(list\/|vs\/)?/, "")
    : null;
  return { pageKind, productSlug };
}

export function trackBuyClickFromPath(args: {
  product: {
    retailer_slug: string;
    delivered_total: string;
    // E46: used to resolve the negotiation this purchase belongs to. Optional
    // so existing narrow call sites still type-check; callers pass the full
    // ProductPageData, which carries both.
    retailer_sku?: string | null;
    product_url?: string | null;
  };
  pathname: string;
}): void {
  const { pageKind, productSlug } = deriveContext(args.pathname);
  trackRetailerBuyClicked({
    retailerSlug: args.product.retailer_slug,
    tfpAud: parseFloat(args.product.delivered_total),
    productSlug,
    pageKind,
    // E46: resolved here so all three row-level Buy buttons are linked
    // without any of them having to know about claims.
    claim: lookupClaim(args.product),
  });
}

export function trackNegotiateFromPath(args: {
  product: { retailer_slug: string; delivered_total: string };
  pathname: string;
}): void {
  const { pageKind, productSlug } = deriveContext(args.pathname);
  trackNegotiateClicked({
    retailerSlug: args.product.retailer_slug,
    tfpAud: parseFloat(args.product.delivered_total),
    productSlug,
    pageKind,
  });
}

/**
 * C20 path-aware wrapper, matching the trackNegotiateFromPath convention so
 * call sites never re-derive page context by hand.
 */
export function trackSignupPromptFromPath(args: {
  retailerSlug: string | null;
  pathname: string;
}): void {
  const { pageKind, productSlug } = deriveContext(args.pathname);
  trackSignupPromptShown({
    source: "negotiate",
    retailerSlug: args.retailerSlug,
    productSlug,
    pageKind,
  });
}

export function trackShareFromPath(args: {
  product: { retailer_slug: string; delivered_total: string };
  pathname: string;
  channel: ShareChannel;
}): void {
  const { pageKind, productSlug } = deriveContext(args.pathname);
  trackShareClicked({
    retailerSlug: args.product.retailer_slug,
    tfpAud: parseFloat(args.product.delivered_total),
    productSlug,
    pageKind,
    channel: args.channel,
  });
}

// ---------- Broadcast negotiation funnel (Day 9) -----------------------

/**
 * The one-button broadcast funnel. The launch IS the experiment, so it is
 * captured from the first send. Offer-received + the reply / offer /
 * discount rates are derived server-side from the claims table (broadcast_id
 * groups the shops; status, timestamps, matched_price_aud and cost_usd are
 * already persisted per shop), so they are NOT events here. None carry PII.
 */

/** The ask panel became visible under the winner (shopCount reachable shops). */
export function trackBroadcastPanelSeen(args: {
  shopCount: number;
  pathname: string;
}): void {
  const { pageKind, productSlug } = deriveContext(args.pathname);
  track("broadcast_panel_seen", {
    shop_count: args.shopCount,
    product_slug: productSlug,
    page_kind: pageKind,
  });
}

/** The user opened the "ask N shops" popup (an intent action, not a walk-away). */
export function trackBroadcastAskOpened(args: {
  shopCount: number;
  pathname: string;
}): void {
  markComparisonAction();
  const { pageKind, productSlug } = deriveContext(args.pathname);
  track("broadcast_ask_opened", {
    shop_count: args.shopCount,
    product_slug: productSlug,
    page_kind: pageKind,
  });
}

/** A broadcast actually went out to shopCount shops. */
export function trackBroadcastSent(args: {
  shopCount: number;
  pathname: string;
}): void {
  markComparisonAction();
  const { pageKind, productSlug } = deriveContext(args.pathname);
  track("broadcast_sent", {
    shop_count: args.shopCount,
    product_slug: productSlug,
    page_kind: pageKind,
  });
}

/** The user took one shop's offer from the requests page. */
export function trackBroadcastOfferAccepted(args: {
  retailerSlug: string;
  tfpAud: number | null;
  transactionId: string | null;
}): void {
  track("broadcast_offer_accepted", {
    retailer_slug: args.retailerSlug,
    tfp_aud: args.tfpAud,
    transaction_id: args.transactionId,
  });
}
